/**
 * 统一的存储管理工具
 * 提供localStorage和sessionStorage的统一接口
 */

// 存储键名常量
export const STORAGE_KEYS = {
  // 用户相关
  TOKEN: 'token',
  EXTERNAL_USER_ID: 'externalUserId',
  TENANT_ID: 'tenantId',
  PHONE: 'phone',
  
  // 企业微信相关
  CORP_ID: 'corpId',
  AGENT_ID: 'agentId',
  
  // 其他业务相关
  USER_PREFERENCES: 'userPreferences',
  THEME: 'theme'
};
// 使用sessionStorage存储的键（备用，通常为空）
const SESSION_STORAGE_KEYS = [
  // 可以根据需要添加特定的临时存储键
];

/**
 * 通用存储类
 */
class StorageManager {
  constructor() {
    this.localStorage = window.localStorage;
    this.sessionStorage = window.sessionStorage;
  }

  /**
   * 根据键名判断应该使用哪种存储方式
   * @param {string} key 存储键名
   * @returns {Storage} localStorage 或 sessionStorage
   */
  getStorageType(key) {
    if (SESSION_STORAGE_KEYS.includes(key)) {
      return this.sessionStorage;
    }
    // 默认都使用localStorage，sessionStorage为备用
    return this.localStorage;
  }

  /**
   * 设置存储值
   * @param {string} key 存储键名
   * @param {any} value 存储值
   * @param {string} storageType 强制指定存储类型：'local' | 'session'
   */
  setItem(key, value, storageType = null) {
    try {
      const storage = storageType === 'local' 
        ? this.localStorage 
        : storageType === 'session' 
          ? this.sessionStorage 
          : this.getStorageType(key);
      const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
      storage.setItem(key, serializedValue);
    } catch (error) {
      console.error(`存储失败 [${key}]:`, error);
    }
  }

  /**
   * 获取存储值
   * @param {string} key 存储键名
   * @param {any} defaultValue 默认值
   * @param {string} storageType 强制指定存储类型：'local' | 'session'
   * @returns {any} 存储的值
   */
  getItem(key, defaultValue = null, storageType = null) {
    try {
      const storage = storageType === 'local' 
        ? this.localStorage 
        : storageType === 'session' 
          ? this.sessionStorage 
          : this.getStorageType(key);
      const value = storage.getItem(key);
      if (value === null) {
        return defaultValue;
      }
      
      // 尝试解析JSON，如果失败则返回原字符串
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error(`获取存储失败 [${key}]:`, error);
      return defaultValue;
    }
  }

  /**
   * 删除存储值
   * @param {string} key 存储键名
   * @param {string} storageType 强制指定存储类型：'local' | 'session'
   */
  removeItem(key, storageType = null) {
    try {
      const storage = storageType === 'local' 
        ? this.localStorage 
        : storageType === 'session' 
          ? this.sessionStorage 
          : this.getStorageType(key);
      
      storage.removeItem(key);
    } catch (error) {
      console.error(`删除存储失败 [${key}]:`, error);
    }
  }

  /**
   * 清空指定类型的存储
   * @param {string} storageType 存储类型：'local' | 'session' | 'all'
   */
  clear(storageType = 'session') {
    try {
      if (storageType === 'local' || storageType === 'all') {
        this.localStorage.clear();
      }
      if (storageType === 'session' || storageType === 'all') {
        this.sessionStorage.clear();
      }
    } catch (error) {
      console.error('清空存储失败:', error);
    }
  }

  /**
   * 获取所有存储的键名
   * @param {string} storageType 存储类型：'local' | 'session'
   * @returns {string[]} 键名数组
   */
  getAllKeys(storageType = 'session') {
    try {
      const storage = storageType === 'local' ? this.localStorage : this.sessionStorage;
      return Object.keys(storage);
    } catch (error) {
      console.error('获取存储键名失败:', error);
      return [];
    }
  }

  /**
   * 检查存储是否可用
   * @param {string} storageType 存储类型：'local' | 'session'
   * @returns {boolean} 是否可用
   */
  isAvailable(storageType = 'session') {
    try {
      const storage = storageType === 'local' ? this.localStorage : this.sessionStorage;
      const testKey = '__storage_test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
}

// 创建单例实例
const storageManager = new StorageManager();

// 导出便捷方法
export const setStorageItem = (key, value, storageType) => 
  storageManager.setItem(key, value, storageType);

export const getStorageItem = (key, defaultValue, storageType) => 
  storageManager.getItem(key, defaultValue, storageType);

export const removeStorageItem = (key, storageType) => 
  storageManager.removeItem(key, storageType);

export const clearStorage = (storageType) => 
  storageManager.clear(storageType);

export const getAllStorageKeys = (storageType) => 
  storageManager.getAllKeys(storageType);

export const isStorageAvailable = (storageType) => 
  storageManager.isAvailable(storageType);

// 导出实例
export default storageManager;